import { useState, useEffect } from 'react';
import { FiX, FiImage, FiUpload } from 'react-icons/fi';
import { inventoryApi } from '../api';

const ProductForm = ({ product, onClose, onSubmit, isSubmitting, activeCategory, currency }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: activeCategory || 'Grains',
    quantity: '',
    minQuantity: '',
    price: '',
    unit: 'kg',
    expiry: '',
    permissibleExpiryDays: '',
    image: null
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [nameExists, setNameExists] = useState(false);
  const [spellingSuggestions, setSpellingSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastValidName, setLastValidName] = useState('');

  const categories = ['Grains', 'Spices', 'Dairy', 'Vegetables', 'Meat'];
  const unitOptions = ['kg', 'lb', 'gal', 'oz', 'fl oz', 'pcs'];

  const debounce = (func, delay) => {
    let timer;
    return function(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        category: product.category,
        quantity: product.current_quantity.toString(),
        minQuantity: product.min_quantity.toString(),
        price: product.price_per_unit.toString(),
        unit: product.unit,
        expiry: product.expiry_date || '',
        permissibleExpiryDays: product.permissible_expiry_days?.toString() || '',
        image: null
      });
      
      if (product.image_url) {
        setImagePreview(product.image_url);
      }
    } else {
      setFormData({
        name: '',
        category: activeCategory || 'Grains',
        quantity: '',
        minQuantity: '',
        price: '',
        unit: 'kg',
        expiry: '',
        permissibleExpiryDays: '',
        image: null
      });
      setImagePreview(null);
    }
    setNameExists(false);
  }, [product, activeCategory]);

  useEffect(() => {
    const checkSpelling = async () => {
      if (formData.name.trim() && formData.name !== lastValidName) {
        try {
          const suggestions = await inventoryApi.getSpellingSuggestions(formData.name);
          setSpellingSuggestions(suggestions);
          setShowSuggestions(suggestions.length > 0);
        } catch (err) {
          console.error('Error getting suggestions:', err);
          setSpellingSuggestions([]);
          setShowSuggestions(false);
        }
      } else {
        setSpellingSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debouncedCheck = debounce(checkSpelling, 500);
    debouncedCheck();

    return () => {
      setSpellingSuggestions([]);
      setShowSuggestions(false);
    };
  }, [formData.name, lastValidName]);

  const handleNameChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, name: value }));
    
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: undefined }));
    }
    
    if (!value.trim()) {
      setSpellingSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setFormData(prev => ({ ...prev, name: suggestion }));
    setLastValidName(suggestion);
    setShowSuggestions(false);
  };

  const validate = async () => {
    const newErrors = {};
    const quantity = parseFloat(formData.quantity);
    const minQuantity = parseFloat(formData.minQuantity);
    const permissibleExpiryDays = formData.permissibleExpiryDays 
      ? parseInt(formData.permissibleExpiryDays) 
      : null;

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (isNaN(quantity)) newErrors.quantity = 'Invalid quantity';
    if (isNaN(minQuantity)) newErrors.minQuantity = 'Invalid minimum quantity';
    if (quantity < 0) newErrors.quantity = 'Cannot be negative';
    if (minQuantity < 0) newErrors.minQuantity = 'Cannot be negative';
    if (isNaN(parseFloat(formData.price))) newErrors.price = 'Invalid price';
    if (formData.permissibleExpiryDays && isNaN(permissibleExpiryDays)) {
      newErrors.permissibleExpiryDays = 'Invalid number of days';
    }
    if (formData.permissibleExpiryDays && permissibleExpiryDays < 0) {
      newErrors.permissibleExpiryDays = 'Cannot be negative';
    }
    
    try {
      const exists = await inventoryApi.checkMaterialName(formData.name);
      if (exists && (!product || formData.name !== product.name)) {
        newErrors.name = 'You already have a material with this name in your inventory';
        setNameExists(true);
      } else {
        setNameExists(false);
      }
    } catch (err) {
      console.error('Error checking name:', err);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validate();
    if (!isValid) return;

    try {
      const form = new FormData();
      form.append('name', formData.name);
      form.append('category', formData.category);
      form.append('current_quantity', formData.quantity);
      form.append('unit', formData.unit);
      form.append('min_quantity', formData.minQuantity);
      form.append('price_per_unit', formData.price);

      if (formData.expiry) {
        form.append('expiry_date', formData.expiry);
      } else if (product?.expiry_date) {
        form.append('expiry_date', '');
      }

      if (formData.permissibleExpiryDays) {
        form.append('permissible_expiry_days', formData.permissibleExpiryDays);
      } else if (product?.permissible_expiry_days) {
        form.append('permissible_expiry_days', '');
      }

      if (formData.image) {
        form.append('image', formData.image);
      } else if (product?.image_url && !imagePreview) {
        form.append('remove_image', 'true');
      }

      await onSubmit(form);
      
    } catch (error) {
      console.error('Detailed error:', error);
      alert(`Error saving product: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({ ...prev, image: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setImagePreview(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold">
            {product ? 'Edit Product' : 'Add New Raw Material'}
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
            disabled={isSubmitting}
          >
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Image</label>
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                {imagePreview ? (
                  <div className="relative group">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="h-16 w-16 object-cover rounded-md border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-sm border border-gray-200 hover:bg-red-100 text-red-500"
                      aria-label="Remove image"
                      disabled={isSubmitting}
                    >
                      <FiX size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="h-16 w-16 flex items-center justify-center bg-gray-100 rounded-md border-2 border-dashed border-gray-300">
                    <FiImage size={18} className="text-gray-400" />
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1 text-center">Max 2MB</p>
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  name="image"
                  onChange={handleImageChange}
                  accept="image/*"
                  className="hidden"
                  id="product-image-upload"
                  disabled={isSubmitting}
                />
                <label
                  htmlFor="product-image-upload"
                  className={`flex items-center justify-center gap-1 w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm cursor-pointer ${
                    isSubmitting ? 'bg-gray-100 cursor-not-allowed' : 'hover:bg-gray-50'
                  }`}
                >
                  <FiUpload size={14} />
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                </label>
              </div>
            </div>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Raw Material Name*</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleNameChange}
              className={`w-full px-2 py-1.5 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              disabled={isSubmitting}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onFocus={() => formData.name && setShowSuggestions(true)}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">{errors.name}</p>
            )}
            
            {showSuggestions && spellingSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                <div className="py-1">
                  <p className="px-3 py-1 text-xs text-gray-500">Did you mean:</p>
                  {spellingSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      className="w-full text-left px-3 py-1 text-sm hover:bg-blue-50"
                      onClick={() => handleSuggestionSelect(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category*</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                required
                disabled={isSubmitting}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit*</label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                required
                disabled={isSubmitting}
              >
                {unitOptions.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Qty*</label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className={`w-full px-2 py-1.5 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm ${
                  errors.quantity ? 'border-red-500' : 'border-gray-300'
                }`}
                required
                min="0"
                step="any"
                disabled={isSubmitting}
              />
              {errors.quantity && (
                <p className="mt-1 text-xs text-red-600">{errors.quantity}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Qty*</label>
              <input
                type="number"
                name="minQuantity"
                value={formData.minQuantity}
                onChange={handleChange}
                className={`w-full px-2 py-1.5 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm ${
                  errors.minQuantity ? 'border-red-500' : 'border-gray-300'
                }`}
                required
                min="0"
                step="any"
                disabled={isSubmitting}
              />
              {errors.minQuantity && (
                <p className="mt-1 text-xs text-red-600">{errors.minQuantity}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Price per Unit*</label>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">{currency.symbol}</span>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className={`flex-1 px-2 py-1.5 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm ${
                  errors.price ? 'border-red-500' : 'border-gray-300'
                }`}
                required
                min="0"
                step="0.01"
                disabled={isSubmitting}
              />
            </div>
            {errors.price && (
              <p className="mt-1 text-xs text-red-600">{errors.price}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
              <input
                type="date"
                name="expiry"
                value={formData.expiry}
                onChange={handleChange}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alert Before (days)</label>
              <input
                type="number"
                name="permissibleExpiryDays"
                value={formData.permissibleExpiryDays}
                onChange={handleChange}
                className={`w-full px-2 py-1.5 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm ${
                  errors.permissibleExpiryDays ? 'border-red-500' : 'border-gray-300'
                }`}
                min="0"
                placeholder="Optional"
                disabled={isSubmitting}
              />
              {errors.permissibleExpiryDays && (
                <p className="mt-1 text-xs text-red-600">{errors.permissibleExpiryDays}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 pb-2 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : (product ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;